import { allPublicPages, corePages, insights } from "./siteContent";
import { localeFromPath, localizePath, localizedInsight, localizedPage, stripLocale, ui, type Locale } from "./i18n";

export const SITE_NAME = "Quantum Maps";
export const DEFAULT_DESCRIPTION = "Engineering-driven spatial intelligence from capture to decision across surveying, LiDAR, navigation, infrastructure, robotics, marine, agriculture, and remote sensing.";
export const DEFAULT_OG_IMAGE = "/manus-storage/qm-hero-1920_4b13f7b9.webp";

export type HeadMeta = {
  title: string;
  description: string;
  canonicalPath?: string;
  ogType?: "website" | "article";
  ogImage?: string;
  ogImageAlt?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
  notFound?: boolean;
  kind?: "webpage" | "service" | "article";
  breadcrumbs?: Array<{ name: string; path: string }>;
  author?: string;
  locale?: Locale;
  alternatePath?: string;
};

const labelFromPath = (path: string) => path.split("/").filter(Boolean).pop()?.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) ?? "Home";

export function cleanPath(url: string) {
  let pathOnly = url.split("?")[0];
  try { pathOnly = decodeURI(pathOnly); } catch { /* mirror browser fail-safe behavior */ }
  return pathOnly.replace(/\/+$/, "") || "/";
}

export function getHeadForPath(url: string): HeadMeta {
  const rawPath = cleanPath(url);
  const locale = localeFromPath(rawPath);
  const path = stripLocale(rawPath);
  const article = insights.find((item) => item.path === path);
  if (article) { const localized=localizedInsight(article,locale); return {
    ...localized.seo,
    ogType: "article",
    ogImage: "/manus-storage/qm-terrain-1600_bfa10f21.webp",
    ogImageAlt: locale==="ar"?`تصور تضاريس وسحابة نقاط لمقال ${localized.title}`:`${localized.title} illustrated with terrain and point-cloud data`,
    publishedTime: article.publishedAt,
    modifiedTime: article.updatedAt,
    kind: "article",
    author: localized.author, locale, alternatePath:localizePath(path,locale==="ar"?"en":"ar"),
    breadcrumbs: [{ name: ui[locale].insights, path: localizePath("/insights",locale) }, { name: localized.title, path:localizePath(path,locale) }],
  }; }

  const content = allPublicPages.find((item) => item.path === path);
  if (content) {
    const localized=localizedPage(content,locale);
    const section = path.startsWith("/industries/") ? ui[locale].industries : path.startsWith("/locations/") ? ui[locale].locations : ui[locale].solutions;
    return {
      ...localized.seo,
      ogType: "website",
      ogImage: DEFAULT_OG_IMAGE,
      ogImageAlt: locale==="ar"?"تضاريس وبنية تحتية تتحول إلى سحابة نقاط وبيانات ليدار":"Terrain and infrastructure transitioning into LiDAR point-cloud and contour data",
      kind: "service",
      locale, alternatePath:localizePath(path,locale==="ar"?"en":"ar"),
      breadcrumbs: [{ name: section, path:localizePath(path,locale) }, { name: localized.navLabel, path:localizePath(path,locale) }],
    };
  }

  const core = corePages[path];
  if (core) { const arabic=AR_CORE[path]; return {
    ...core,
    ...(locale==="ar"&&arabic?arabic:{}),
    canonicalPath:localizePath(path,locale),
    noindex: core.noIndex,
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: locale==="ar"?"تضاريس وبنية تحتية تتحول إلى بيانات مكانية":"Terrain and infrastructure transitioning into LiDAR point-cloud and contour data",
    kind: "webpage",
    locale, alternatePath:localizePath(path,locale==="ar"?"en":"ar"),
    breadcrumbs: path === "/" ? [] : [{ name: locale==="ar"?(AR_LABELS[path]||labelFromPath(path)):labelFromPath(path), path:localizePath(path,locale) }],
  }; }

  return {
    title: locale==="ar"?`الصفحة غير موجودة | ${SITE_NAME}`:`Page Not Found | ${SITE_NAME}`,
    description: locale==="ar"?"تعذر العثور على صفحة Quantum Maps المطلوبة.":"The requested Quantum Maps page could not be found.",
    noindex: true,
    notFound: true,
    kind: "webpage",
    locale,
  };
}

const AR_LABELS:Record<string,string>={"/technology":"التقنيات","/projects":"المشاريع","/insights":"المعرفة","/about":"عن الشركة","/beyond-todays-maps":"البحث والتطوير","/contact":"تواصل معنا","/privacy":"سياسة الخصوصية","/terms":"شروط الاستخدام","/cookies":"سياسة ملفات الارتباط","/portal":"بوابة العملاء"};
const AR_CORE:Record<string,{title:string;description:string}>={
  "/":{title:"Quantum Maps | هندسة الذكاء المكاني",description:"ذكاء مكاني هندسي من الالتقاط إلى القرار عبر المساحة والليدار والملاحة والبنية التحتية والروبوتات والبحر والزراعة والاستشعار عن بُعد."},
  "/technology":{title:"منظومة التقنيات الجيومكانية | Quantum Maps",description:"منظومة مترابطة للطائرات المسيّرة والليدار وGNSS وINS والتصوير والمعالجة."},
  "/projects":{title:"دراسات المشاريع الجيومكانية | Quantum Maps",description:"نظام دراسات حالة جاهز للمشاريع الموثقة، مع قوالب واضحة قابلة للتحرير دون ادعاءات غير مؤكدة."},
  "/insights":{title:"المعرفة الهندسية الجيومكانية | Quantum Maps",description:"مقالات تقنية حول الليدار والطائرات المسيّرة وGNSS/INS والالتقاط ثلاثي الأبعاد والملاحة."},
  "/about":{title:"عن Quantum Maps | الهندسة الجيومكانية",description:"شركة هندسية وتقنية تربط الاستشعار وتحديد المواقع ورسم الخرائط والبيانات ثلاثية الأبعاد."},
  "/beyond-todays-maps":{title:"ما بعد خرائط اليوم | بحث وتطوير Quantum Maps",description:"اتجاهات بحثية في دمج المستشعرات والملاحة المرنة وSLAM والخرائط الذاتية والتوائم الرقمية."},
  "/contact":{title:"ابدأ مشروعاً جيومكانياً | Quantum Maps",description:"ناقش مشروع ليدار أو طائرات مسيّرة أو GNSS/INS أو نمذجة ثلاثية الأبعاد مع Quantum Maps."},
  "/privacy":{title:"سياسة الخصوصية | Quantum Maps",description:"قالب سياسة الخصوصية بانتظار مراجعة المالك والمستشار القانوني."},
  "/terms":{title:"شروط الاستخدام | Quantum Maps",description:"قالب شروط الاستخدام بانتظار مراجعة المالك والمستشار القانوني."},
  "/cookies":{title:"سياسة ملفات الارتباط | Quantum Maps",description:"قالب سياسة ملفات الارتباط بانتظار مراجعة المالك والمستشار القانوني."},
  "/portal":{title:"بوابة العملاء | Quantum Maps",description:"قالب مستقبلي لبوابة عملاء Quantum Maps."},
};

export function buildStructuredData(head: HeadMeta, origin: string) {
  const canonicalUrl = head.canonicalPath ? `${origin}${head.canonicalPath}` : origin;
  const language = head.locale === "ar" ? "ar" : "en";
  const organization = {
    "@type": "Organization",
    "@id": `${origin}/#organization`,
    name: SITE_NAME,
    url: origin,
    description: DEFAULT_DESCRIPTION,
    areaServed: ["Canada", "Jordan"],
  };
  const website = { "@type": "WebSite", "@id": `${origin}/#website`, url: origin, name: SITE_NAME, inLanguage:["en","ar"], publisher: { "@id": `${origin}/#organization` } };
  const webpage = { "@type": "WebPage", "@id": `${canonicalUrl}#webpage`, url: canonicalUrl, name: head.title, description: head.description, inLanguage:language, isPartOf: { "@id": `${origin}/#website` }, about: { "@id": `${origin}/#organization` } };
  const graph: Record<string, unknown>[] = [organization, website, webpage];

  if (head.breadcrumbs?.length) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${canonicalUrl}#breadcrumb`,
      itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: origin }, ...head.breadcrumbs.map((item, index) => ({ "@type": "ListItem", position: index + 2, name: item.name, item: `${origin}${item.path}` }))],
    });
  }

  if (head.kind === "service") graph.push({ "@type": "Service", "@id": `${canonicalUrl}#service`, name: head.title.replace(` | ${SITE_NAME}`, ""), description: head.description, provider: { "@id": `${origin}/#organization` }, areaServed: ["Canada", "Jordan"] });
  if (head.kind === "article") graph.push({ "@type": "Article", "@id": `${canonicalUrl}#article`, headline: head.title.replace(` | ${SITE_NAME}`, ""), description: head.description, datePublished: head.publishedTime, dateModified: head.modifiedTime, author: { "@type": "Organization", name: head.author || SITE_NAME }, publisher: { "@id": `${origin}/#organization` }, mainEntityOfPage: { "@id": `${canonicalUrl}#webpage` } });
  return { "@context": "https://schema.org", "@graph": graph };
}
